import { EditTwoTone, EyeFilled, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Row, Col, Button, Select, Table, Spin, DatePicker } from "antd";
import { ColumnsType } from "antd/es/table";
import React from "react";
import {
  EmployeePayFrequency,
  EmployeePayFrequencyOptionList,
  INIThresholds,
} from "../../Services/PayrollYearlySettingsService";
import dayjs, { Dayjs } from "dayjs";
import Link from "antd/es/typography/Link";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";

interface iNIThersholdsProps {
  isViewOnly: boolean;
  OnCancelClick: () => void;
  setDialog: React.Dispatch<
    React.SetStateAction<{
      type: string;
      params: any;
    }>
  >;
  NIThresholdsList: INIThresholds[] | [];
}

const NIThersholds = ({
  isViewOnly = false,
  OnCancelClick,
  setDialog,
  NIThresholdsList,
}: iNIThersholdsProps) => {
  const [loading, setLoading] = React.useState(false);
  const [niThersholdslistData, setNIThersholdsListData] = React.useState(
    NIThresholdsList || []
  );
  const [params, setParams] = React.useState<{
    payFrequency: EmployeePayFrequency[];
    director: number;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  }>({
    payFrequency: [],
    director: 0,
    fromDate: null,
    toDate: null,
  });
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 15,
  });

  const columns: ColumnsType<INIThresholds> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "4%",
      render: (_: any, record: any, index: any) => <p>{(pagination.current - 1) * pagination.pageSize + index + 1}</p>,
    },
    {
      title: "Pay Frequency",
      dataIndex: "payFrequency",
      key: "PayFrequency",
      width: "9%",
      render: (_: any, record: any, index: any) => (
        <p>{EmployeePayFrequency[record?.payFrequency]}</p>
      ),
    },
    {
      title: "Director",
      dataIndex: "director",
      key: "Director",
      width: "7%",
      render: (_: any, record: any, index: any) => (
        <>{record?.director ? "Yes" : "No"}</>
      ),
    },
    {
      title: "PayDate From",
      dataIndex: "payDateFrom",
      key: "PayDateFrom",
      width: "10%",
      render: (_: any, record: any, index: any) => (
        <>{record?.payDateFrom ? dayjs(record?.payDateFrom).format("DD/MM/YYYY") : "-"}</>
      ),
      sorter: (a, b) =>
        dayjs(a.payDateFrom).unix() - dayjs(b.payDateFrom).unix(),
    },
    {
      title: "PayDate To",
      dataIndex: "payDateTo",
      key: "PayDateTo",
      width: "10%",
      render: (_: any, record: any, index: any) => (
        <>{record?.payDateTo ? dayjs(record?.payDateTo).format("DD/MM/YYYY") : "-"}</>
      ),
      sorter: (a, b) =>
        dayjs(a.payDateTo).unix() - dayjs(b.payDateTo).unix(),
    },
    {
      title: "LEL",
      dataIndex: "lel",
      key: "lel",
      width: "7%",
      sorter: (a, b) => a.lel - b.lel,
    },
    {
      title: "UEL",
      dataIndex: "uel",
      key: "uel",
      width: "7%",
      sorter: (a, b) => a.uel - b.uel,
    },
    {
      title: "AUEL",
      dataIndex: "auel",
      key: "auel",
      width: "7%",
      sorter: (a, b) => a.auel - b.auel,
    },
    {
      title: "PT",
      dataIndex: "pt",
      key: "pt",
      width: "7%",
      sorter: (a, b) => a.pt - b.pt,
    },
    {
      title: "ST",
      dataIndex: "st",
      key: "st",
      width: "7%",
      sorter: (a, b) => a.st - b.st,
    },
    {
      title: "UST",
      dataIndex: "ust",
      key: "ust",
      width: "6%",
      sorter: (a, b) => a.ust - b.ust,
    },
    {
      title: "AUST",
      dataIndex: "aust",
      key: "aust",
      width: "6%",
      sorter: (a, b) => a.aust - b.aust,
    },
    {
      title: "FUST",
      dataIndex: "fust",
      key: "fust",
      width: "6%",
      sorter: (a, b) => a.fust - b.fust,
    },
    {
      title: "VUST",
      dataIndex: "vust",
      key: "vust",
      width: "6%",
      sorter: (a, b) => a.vust - b.vust,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "Id",
      width: "3%",
      render: (_: any, record: any, index: any) => (
        <Link
          className="edit-btn"
          onClick={() => {
            setDialog({
              type: "AddNIThersholds",
              params: { record: record },
            });
          }}
        >
          {
            isViewOnly ?
              <EyeFilled />
              :
              <EditTwoTone />
          }
        </Link>
      ),
    },
  ];

  React.useEffect(() => {
    if (NIThresholdsList?.length > 0) {
      setLoading(true);

      let lst = NIThresholdsList;

      const hasPayFreq =
        Array.isArray(params.payFrequency) &&
        params.payFrequency.length > 0;

      const hasDateRange = params.fromDate && params.toDate;

      lst = lst.filter((ni: any) => {

        /* -------- Director Filter -------- */
        let directorMatch = true;
        if (params.director === 1) directorMatch = ni.director === true;
        else if (params.director === 2) directorMatch = ni.director === false || ni.director === null;

        /* -------- Pay Frequency Filter -------- */
        const payFreqMatch = hasPayFreq
          ? params.payFrequency.includes(ni.payFrequency)
          : true;

        /* -------- Date Range Filter -------- */
        let dateMatch = true;
        if (hasDateRange) {
          const recordFrom = dayjs(ni.payDateFrom);
          const recordTo = dayjs(ni.payDateTo);

          dateMatch =
            recordFrom.isSameOrBefore(params.toDate, "day") &&
            recordTo.isSameOrAfter(params.fromDate, "day");
        }

        return directorMatch && payFreqMatch && dateMatch;
      });

      setNIThersholdsListData(lst);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, NIThresholdsList]);

  const setListParamsAndRefresh = (key: any, value: any) => {
    // setPagingItems({ ...pagingItems, currentPage: 1, pageSize: 15 })
    setParams((p: any) => ({ ...p, [key]: value }));
  };
  return (
    <>
      <Row
        className="p-3"
        style={{
          backgroundColor: "white",
          paddingTop: 2
          // minHeight: window?.outerHeight - 196.6,
        }}
      >
        <Col span={24}>
          <Row gutter={[8, 8]} className="pb-3"
            align="middle">
            {/* ================= ADD ================= */}
            <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
              {
                !isViewOnly ?
                  <Button
                    block
                    type="primary"
                    className="add-btn"
                    onClick={() => setDialog({ type: "AddNIThersholds", params: {} })}
                  >
                    <PlusOutlined />
                    Add NI Thersholds
                  </Button>
                  : null
              }
            </Col>
            {/* ================= DATE ================= */}
            <Col xs={24} sm={12} md={{ span: 7, offset: 0 }} lg={{ span: 6, offset: 4 }} xl={{ span: 5, offset: 7 }} xxl={{ span: 4, offset: 11 }}>
              <CustomRangePicker
                value={[params.fromDate, params.toDate]}
                onChange={(dates) => {
                  setParams((prev) => ({
                    ...prev,
                    fromDate: dates?.[0] || null,
                    toDate: dates?.[1] || null,
                  }));
                }}
              />
            </Col>
            {/* ================= PRACTICES ================= */}
            <Col xs={24} sm={12} md={7} lg={6} xl={5} xxl={4}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Select Pay Frequency"
                maxTagCount="responsive"
                style={{ width: "100%" }}
                onChange={(val) => setListParamsAndRefresh("payFrequency", val)}
                filterOption={(input, option) =>
                  (option?.label?.toString() ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={EmployeePayFrequencyOptionList}
              />
            </Col>
            {/* ================= STATUS ================= */}
            <Col xs={24} sm={12} md={4} lg={3} xl={3} xxl={2}>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Director Exist"
                options={[
                  { value: 0, label: "All Director" },
                  { value: 1, label: "Yes Director" },
                  { value: 2, label: "No Director" },
                ]}
                value={params?.director}
                onChange={(val) => setParams({ ...params, director: val })}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24} >
          <Table
            dataSource={niThersholdslistData}
            columns={columns}
            size="small"
            // className="aoa-custom-table"
            className="aoa-custom-table border-top-table"

            loading={{
              spinning: loading,
              indicator: (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
                />
              ),
            }}
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            pagination={{
              defaultPageSize: 15,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              showSizeChanger: true,
              total: niThersholdslistData?.length,
              pageSizeOptions: ["15", "25", "50", "100"],
              onChange: (page, pageSize) =>
                setPagination({ current: page, pageSize }),
            }}
            scroll={{ x: 700 }}
          />
        </Col>
      </Row>
    </>
  );
};

export default NIThersholds;
